import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import Breadcrumbs from "../../common/Breadcrumbs";
import {
  HomeOutlinedIcon,
  AddIcon,
  FolderIcon,
  FileIcon,
  ArrowBackIcon,
  ArrowLeftIcon,
} from "../../../icons";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import GlobalLoader from "../../common/GlobalLoader";
import GenerateQuestionBankPopup from "../../common/GenerateQuestionBankPopup";
import QuestionBankViewPopup from "../../common/QuestionBankViewPopup";
import {
  getFolders,
  getQuestionBanks,
  createSubtopicFolder,
  updateFolder,
  deleteFolder,
  updateInstitutionQuestionBankFolder,
  deleteInstitutionQuestionBankFolder,
  getAdminQuestionBankFoldersInInstitution,
  getAdminQuestionBanksInInstitution,
  getInstitutionQuestionBanks,
} from "../../../helper/apicalls/questionBank";
import CreateFolderPopup from "../../common/CreateFolderPopup";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import "./../../../styles/admin/questionBank.scss";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";

const QuestionBankSubtopicFiles = () => {
  const { courseKey, subjectKey, topicKey, subtopicKey } = useParams();
  const location = useLocation();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isTempFolder = subtopicKey?.includes("-temp-");
  const [showGeneratePopup, setShowGeneratePopup] = useState(false);
  const [activeFolder, setActiveFolder] = useState(null);
  const [folderBanks, setFolderBanks] = useState([]);
  const [viewBankId, setViewBankId] = useState(null);
  const [isViewPopupOpen, setIsViewPopupOpen] = useState(false);
  const [folderMenuOpenId, setFolderMenuOpenId] = useState(null);
  const [showCreateFolderPopup, setShowCreateFolderPopup] = useState(false);
  const [editFolder, setEditFolder] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);

  const isInstitution =
    location.pathname.startsWith("/institution") ||
    location.pathname.startsWith("/teacher");

  const role = Cookies.get("role");
  const permissions = Cookies.get("permissions");
  let permissionsData = [];
  try {
    const parsed = permissions ? JSON.parse(permissions) : [];
    permissionsData = Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error("Error parsing permissions:", e);
  }
  const questionBankPermission = permissionsData?.find(
    (permission) => permission?.permission_type === "questionbank"
  );
  const hasWritePermission =
    role === "admin" || (questionBankPermission ? !!questionBankPermission.write : false);

  const handleClickOutside = (event) => {
    const menus = document.querySelectorAll(
      ".folder-menu, .folder-menu-button"
    );
    for (let i = 0; i < menus.length; i += 1) {
      const el = menus[i];
      if (el && el.contains(event.target)) {
        return;
      }
    }
    setFolderMenuOpenId(null);
  };

  useEffect(() => {
    if (!folderMenuOpenId) return;

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [folderMenuOpenId]);

  const handleAddFolder = () => {
    setShowCreateFolderPopup(true);
  };

  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      if (isTempFolder) {
        setFiles([]);
        return;
      }

      let data = [];
      if (isInstitution) {
        const adminFolders = await getAdminQuestionBankFoldersInInstitution("subtopic", subtopicKey);
        const institutionFolders = await getFolders("subtopic", subtopicKey);
        data = [
          ...adminFolders.map(f => ({ ...f, is_admin: true })),
          ...institutionFolders.map(f => ({ ...f, is_admin: false }))
        ];
      } else {
        data = await getFolders("subtopic", subtopicKey);
      }
      setFiles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching subtopic question bank files:", err);
      setError("Failed to fetch subtopic question bank files");
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const closeFolderMenu = () => {
    setFolderMenuOpenId(null);
  };

  const handleFolderMenuToggle = (id, e) => {
    e.stopPropagation();
    setFolderMenuOpenId((prev) => (prev === id ? null : id));
  };

  const handleFolderPublish = (folder, e) => {
    e.stopPropagation();
    console.log("Publish folder", folder);
    closeFolderMenu();
  };

  const handleFolderEdit = (folder, e) => {
    e.stopPropagation();
    setEditFolder(folder);
    closeFolderMenu();
  };

  const handleFolderDelete = (folder, e) => {
    e.stopPropagation();
    setFolderToDelete(folder);
    setIsConfirmDeleteOpen(true);
    closeFolderMenu();
  };

  const handleQuestionBankClick = (qb) => {
    if (!qb || !qb.id) return;
    setViewBankId(qb.id);
    setIsViewPopupOpen(true);
  };

  const handleBackFromFolder = () => {
    setActiveFolder(null);
    setFolderBanks([]);
  };

  const handleFolderClick = async (folder) => {
    try {
      setLoading(true);
      setError(null);
      setActiveFolder(folder);
      let data;
      if (folder.is_admin) {
        data = await getAdminQuestionBanksInInstitution(folder.id);
        if (Array.isArray(data)) {
          data = data.map((qb) => ({ ...qb, is_admin: true }));
        }
      } else {
        const listQuestionBanks = isInstitution
          ? getInstitutionQuestionBanks
          : getQuestionBanks;
        data = await listQuestionBanks(folder.id);
        if (Array.isArray(data)) {
          data = data.map((qb) => ({ ...qb, is_admin: false }));
        }
      }
      setFolderBanks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching question banks for folder:", err);
      setError("Failed to fetch question banks");
      setFolderBanks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [courseKey, subjectKey, topicKey, subtopicKey, location]);
  return (
    <>
      <GlobalLoader visible={loading} text="Loading..." />
      <div className="question-bank-container">
        <div className="qb-card">
          <div className="qb-card-header">
            {activeFolder && (
              <button
                type="button"
                className="qb-back-icon-btn"
                onClick={handleBackFromFolder}
              >
                <ArrowBackIcon />
              </button>
            )}
            <span>Subtopic Question Bank</span>
            <div className="qb-card-actions">
              {!activeFolder && !isTempFolder && hasWritePermission && (
                <button
                  type="button"
                  className="qb-add-btn"
                  onClick={handleAddFolder}
                >
                  <AddIcon className="action-button-icon" />
                  <span>Add Folder</span>
                </button>
              )}
              {activeFolder && !activeFolder.is_admin && hasWritePermission && (
                <button
                  type="button"
                  className="qb-add-btn"
                  onClick={() => setShowGeneratePopup(true)}
                >
                  <AddIcon className="action-button-icon" />
                  <span>Add Question Bank</span>
                </button>
              )}
            </div>
          </div>

          {!activeFolder && (
            <div className="folder-grid">
              {!loading &&
                !error &&
                !isTempFolder &&
                files?.map((folder) => {
                  const id = folder.id || folder.key;
                  return (
                    <div
                      key={id}
                      className="folder-item"
                      onClick={() => handleFolderClick(folder)}
                    >
                      <div className="folder-icon">
                        <FolderIcon style={{ color: "var(--folder-bg)" }} />
                      </div>
                      <div className="folder-name">{folder.name}</div>
                      {isInstitution && folder.is_admin && (
                        <div className="qb-default-badge">Default</div>
                      )}
                      {!folder.is_admin && hasWritePermission && (
                        <button
                          type="button"
                          className="folder-menu-button"
                          onClick={(e) => handleFolderMenuToggle(id, e)}
                        >
                          <MoreVertIcon />
                        </button>
                      )}
                      {folderMenuOpenId === id && (
                        <div
                          className="folder-menu"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div
                            className="folder-menu-item"
                            onClick={(e) => handleFolderPublish(folder, e)}
                          >
                            Publish
                          </div>
                          <div
                            className="folder-menu-item"
                            onClick={(e) => handleFolderEdit(folder, e)}
                          >
                            Edit
                          </div>
                          <div
                            className="folder-menu-item"
                            onClick={(e) => handleFolderDelete(folder, e)}
                          >
                            Delete
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

              {!loading && !error && isTempFolder && (
                <div
                  className="folder-item file-item"
                  onClick={() => setShowGeneratePopup(true)}
                >
                  <div className="folder-icon">
                    <AddCircleOutlineOutlinedIcon className="add-course-icon" />
                  </div>
                  <div className="folder-name">Add Question Bank</div>
                </div>
              )}
            </div>
          )}

          {activeFolder && (
            <div className="folder-grid">
              {!loading && !error && !activeFolder.is_admin && hasWritePermission && (
                <div
                  className="folder-item file-item"
                  onClick={() => setShowGeneratePopup(true)}
                >
                  <div className="folder-icon">
                    <AddIcon />
                  </div>
                  <div className="folder-name">Add Question Bank</div>
                </div>
              )}

              {!loading &&
                !error &&
                folderBanks?.map((qb) => (
                  <div
                    key={qb.id}
                    className="folder-item file-item"
                    onClick={() => handleQuestionBankClick(qb)}
                  >
                    <div className="folder-icon">
                      <FileIcon style={{ color: "var(--text-secondary)" }} />
                    </div>
                    <div className="folder-name">{qb.name || qb.id}</div>
                    {isInstitution && qb.is_admin && (
                      <div className="qb-default-badge">Default</div>
                    )}
                    {qb.published && (
                      <div className="folder-published">Published</div>
                    )}
                  </div>
                ))}

              {error && <div className="qb-error">{error}</div>}
            </div>
          )}
        </div>
      </div>

     {showGeneratePopup && <GenerateQuestionBankPopup
        open={showGeneratePopup}
        handleClose={() => setShowGeneratePopup(false)}
        context="subtopic"
        contextKey={subtopicKey}
        folderId={activeFolder ? activeFolder.id : null}
        onSave={(payload) => {
          setShowGeneratePopup(false);
          if (activeFolder) {
            handleFolderClick(activeFolder);
          } else {
            fetchFiles();
          }
        }}
      />}

     {isViewPopupOpen && <QuestionBankViewPopup
        open={isViewPopupOpen}
        questionBankId={viewBankId}
        context="subtopic"
        contextKey={subtopicKey}
        isAdminBank={activeFolder?.is_admin || false}
        onClose={() => {
          setIsViewPopupOpen(false);
          setViewBankId(null);
        }}
      />}

      {showCreateFolderPopup && (
        <CreateFolderPopup
          open={showCreateFolderPopup}
          handleClose={() => setShowCreateFolderPopup(false)}
          title="subtopic"
          contextKey={subtopicKey}
          mode="create"
          onSave={async (payload) => {
            try {
              setLoading(true);
              await createSubtopicFolder(
                payload.title,
                payload.key,
                payload.name
              );
              const foldersData = await getFolders("subtopic", subtopicKey);
              setFiles(Array.isArray(foldersData) ? foldersData : []);
            } catch (err) {
              console.error("Error creating subtopic folder:", err);
              setError("Failed to create folder");
            } finally {
              setLoading(false);
            }
          }}
        />
      )}

      {!!editFolder && (
        <CreateFolderPopup
          open={!!editFolder}
          handleClose={() => setEditFolder(null)}
          title="subtopic"
          contextKey={subtopicKey}
          mode="edit"
          initialName={editFolder?.name || ""}
          folderId={editFolder?.id}
          onSave={async (payload) => {
            try {
              if (!payload.id) return;
              setLoading(true);
              if (isInstitution) {
                await updateInstitutionQuestionBankFolder({
                  id: payload.id,
                  name: payload.name,
                });
              } else {
                await updateFolder(payload.id, payload.name);
              }
              const foldersData = await getFolders("subtopic", subtopicKey);
              setFiles(Array.isArray(foldersData) ? foldersData : []);
            } catch (err) {
              console.error("Error updating subtopic folder:", err);
              setError("Failed to update folder");
            } finally {
              setLoading(false);
              setEditFolder(null);
            }
          }}
        />
      )}

      {isConfirmDeleteOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isConfirmDeleteOpen}
          setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
          title="Delete folder"
          message={`Are you sure you want to delete the folder "${
            folderToDelete?.name || ""
          }"? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          handleDelete={async () => {
            if (!folderToDelete?.id) {
              setIsConfirmDeleteOpen(false);
              return;
            }
            try {
              setLoading(true);
              if (isInstitution) {
                await deleteInstitutionQuestionBankFolder(folderToDelete.id);
              } else {
                await deleteFolder(folderToDelete.id);
              }
              const foldersData = await getFolders("subtopic", subtopicKey);
              setFiles(Array.isArray(foldersData) ? foldersData : []);
            } catch (err) {
              console.error("Error deleting subtopic folder:", err);
              setError("Failed to delete folder");
            } finally {
              setLoading(false);
              setIsConfirmDeleteOpen(false);
              setFolderToDelete(null);
            }
          }}
        />
      )}
    </>
  );
};

export default QuestionBankSubtopicFiles;
